import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FiSearch, FiFilter, FiChevronUp, FiChevronDown,
  FiFileText, FiBarChart2, FiRefreshCw,
} from 'react-icons/fi';
import { ouvrirDocumentStocke } from '../../utils/documentStocke';

// ─── Helpers ──────────────────────────────────────────────────────────────────
function parseMetadata(raw) {
  if (!raw) return {};
  try { return typeof raw === 'string' ? JSON.parse(raw) : raw; }
  catch { return {}; }
}

function formatDate(dateStr) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('fr-FR', {
    day: '2-digit', month: '2-digit', year: 'numeric',
  });
}

function resolveCompanyName(contract) {
  return (
    contract?.account?.company_name ||
    parseMetadata(contract?.metadata).company_name ||
    null
  );
}

function resolveInitials(contract) {
  const name = resolveCompanyName(contract);
  if (!name) return '?';
  return name.split(' ').filter(Boolean).slice(0, 2).map(w => w[0].toUpperCase()).join('');
}

// ─── StatusDot ────────────────────────────────────────────────────────────────
const DOT_STYLES = {
  inactive: 'bg-gray-200 border-gray-300',
  active:   'bg-blue-400 border-blue-500',
  closed:   'bg-teal-400  border-teal-500',
};
const StatusDot = ({ active, closed, title }) => {
  const s = closed ? 'closed' : active ? 'active' : 'inactive';
  return (
    <span
      title={title}
      className={`inline-block w-2.5 h-2.5 rounded-full border transition-colors ${DOT_STYLES[s]}`}
    />
  );
};

// ─── Composant principal ──────────────────────────────────────────────────────
/**
 * Props :
 *  - selectedYear   : number  (ex. 2026)
 *  - selectedMonth  : number  (0 = tous, 1-12 = mois)
 *  - session        : objet Supabase session  { access_token }
 *  - onSelectClient : (contract) => void  — callback quand on clique "Voir TICGN"
 */
const SignedContractsTICGN = ({
  selectedYear,
  selectedMonth = 0,
  session,
  onSelectClient,
}) => {
  const navigate = useNavigate();

  const [contracts, setContracts] = useState([]);
  const [loading,   setLoading]   = useState(false);
  const [error,     setError]     = useState(null);

  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({ search: '', rum: '', taxes: '' });
  const [sortConfig, setSortConfig] = useState({ key: 'signed_at', direction: 'desc' });

  // ── Fetch ──────────────────────────────────────────────────────────────────
  const fetchContracts = useCallback(async () => {
    if (!session?.access_token) return;
    setLoading(true);
    setError(null);
    try {
      const res  = await fetch(
        `/api/documents?type=tax_contract&status=signed`,
        { headers: { Authorization: `Bearer ${session.access_token}` } }
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      const all  = json.data || [];

      // Filtre client-side sur signed_at
      const filtered = all.filter(c => {
        if (!c.signed_at) return false;
        const d = new Date(c.signed_at);
        const yearOk  = d.getFullYear() === selectedYear;
        const monthOk = !selectedMonth || selectedMonth === 0
          || (d.getMonth() + 1) === selectedMonth;
        return yearOk && monthOk;
      });

      setContracts(filtered);
    } catch (err) {
      console.error('[SignedContractsTICGN] Erreur fetch :', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [session, selectedYear, selectedMonth]);

  useEffect(() => {
    fetchContracts();
  }, [fetchContracts]);

  // ── Tri ───────────────────────────────────────────────────────────────────
  const handleSort = (key) =>
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }));

  const SortableHeader = ({ label, colKey, className = '' }) => (
    <th
      onClick={() => handleSort(colKey)}
      className={`px-2 py-1.5 text-left text-[10px] font-semibold text-gray-500
                  uppercase tracking-wider cursor-pointer select-none hover:bg-gray-100 ${className}`}
    >
      <span className="flex items-center gap-1">
        {label}
        {sortConfig.key === colKey
          ? sortConfig.direction === 'asc'
            ? <FiChevronUp   className="w-3 h-3 text-teal-600" />
            : <FiChevronDown className="w-3 h-3 text-teal-600" />
          : <FiChevronDown className="w-3 h-3 text-gray-300" />}
      </span>
    </th>
  );

  // ── Filtres + tri ──────────────────────────────────────────────────────────
  const rows = useMemo(() => {
    let data = [...contracts];

    if (filters.search) {
      const q = filters.search.toLowerCase();
      data = data.filter(c => {
        const name = resolveCompanyName(c) || '';
        const meta = parseMetadata(c.metadata);
        return (
          name.toLowerCase().includes(q) ||
          (meta.siret || '').toLowerCase().includes(q) ||
          (c.account_id || '').toLowerCase().includes(q)
        );
      });
    }
    if (filters.rum) {
      data = data.filter(c =>
        (parseMetadata(c.metadata).rum || '').toLowerCase().includes(filters.rum.toLowerCase())
      );
    }
    if (filters.taxes) {
      data = filters.taxes === 'oui' ? data : [];
    }

    data.sort((a, b) => {
      const av = sortConfig.key === 'company_name'
        ? resolveCompanyName(a) || ''
        : a[sortConfig.key] ?? '';
      const bv = sortConfig.key === 'company_name'
        ? resolveCompanyName(b) || ''
        : b[sortConfig.key] ?? '';
      const cmp = String(av).localeCompare(String(bv), 'fr', { numeric: true });
      return sortConfig.direction === 'asc' ? cmp : -cmp;
    });

    return data;
  }, [contracts, filters, sortConfig]);

  const activeFiltersCount = Object.values(filters).filter(Boolean).length;

  // ── Rendu ──────────────────────────────────────────────────────────────────
  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">

      {/* En-tête */}
      <div className="px-3 py-2 border-b border-gray-200 flex items-center justify-between">
        <div>
          <h2 className="text-xs font-semibold text-gray-800 flex items-center gap-1.5">
            Contrats signés — TICGN
            <span className="px-1.5 py-0.5 bg-blue-50 text-blue-700 rounded text-[9px] font-medium">
              {selectedYear}{selectedMonth ? ` · M${selectedMonth}` : ''}
            </span>
            {activeFiltersCount > 0 && (
              <span className="px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded text-[9px]">
                {activeFiltersCount} filtre{activeFiltersCount > 1 ? 's' : ''}
              </span>
            )}
          </h2>
          <p className="text-[9px] text-gray-400 mt-0.5">
            {loading ? 'Chargement…' : `${rows.length} contrat${rows.length !== 1 ? 's' : ''}`}
          </p>
        </div>

        <div className="flex items-center gap-1.5">
          {/* Recherche */}
          <div className="relative">
            <FiSearch className="absolute left-1.5 top-1/2 -translate-y-1/2 w-2.5 h-2.5 text-gray-400" />
            <input
              type="text"
              placeholder="Rechercher…"
              value={filters.search}
              onChange={e => setFilters(p => ({ ...p, search: e.target.value }))}
              className="pl-5 pr-2 py-1 border border-gray-200 rounded text-[10px] w-32
                         focus:outline-none focus:ring-1 focus:ring-teal-400"
            />
          </div>
          {/* Filtres */}
          <button
            onClick={() => setShowFilters(p => !p)}
            className={`px-1.5 py-1 rounded text-[10px] border flex items-center gap-1 transition-colors ${
              showFilters
                ? 'bg-teal-50 border-teal-300 text-teal-700'
                : 'bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100'
            }`}
          >
            <FiFilter className="w-2.5 h-2.5" /> Filtres
          </button>
          {/* Reset */}
          {activeFiltersCount > 0 && (
            <button
              onClick={() => setFilters({ search: '', rum: '', taxes: '' })}
              className="px-1.5 py-1 text-[10px] text-gray-500 hover:text-red-500
                         border border-gray-200 rounded"
            >✕ Reset</button>
          )}
          {/* Refresh */}
          <button
            onClick={fetchContracts}
            disabled={loading}
            title="Rafraîchir"
            className="inline-flex items-center justify-center w-6 h-6 rounded bg-gray-50
                       border border-gray-200 text-gray-500 hover:bg-gray-100 disabled:opacity-40"
          >
            <FiRefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Filtres avancés */}
      {showFilters && (
        <div className="px-2 py-1 bg-gray-50 border-b border-gray-100 flex flex-wrap gap-1.5 items-center">
          <input
            placeholder="RUM…"
            value={filters.rum}
            onChange={e => setFilters(p => ({ ...p, rum: e.target.value }))}
            className="py-0.5 px-1.5 border border-gray-200 rounded text-[10px] w-24
                       focus:outline-none focus:ring-1 focus:ring-teal-300"
          />
          <select
            value={filters.taxes}
            onChange={e => setFilters(p => ({ ...p, taxes: e.target.value }))}
            className="py-0.5 px-1 border border-gray-200 rounded text-[10px] focus:outline-none"
          >
            <option value="">Taxes</option>
            <option value="oui">Oui</option>
            <option value="non">Non</option>
          </select>
        </div>
      )}

      {/* Erreur */}
      {error && (
        <div className="px-3 py-2 bg-red-50 border-b border-red-100 text-[10px] text-red-600">
          Erreur de chargement : {error}
        </div>
      )}

      {/* Tableau */}
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <SortableHeader label="Client"   colKey="company_name" />
              <SortableHeader label="SIRET"    colKey="siret"        />
              <SortableHeader label="RUM"      colKey="rum"          />
              <SortableHeader label="Signé le" colKey="signed_at"    />
              <th className="px-2 py-1.5 text-left text-[10px] font-semibold uppercase
                             tracking-wider bg-blue-50 text-blue-700">
                TICGN
              </th>
              <th className="px-2 py-1.5 text-left text-[10px] font-semibold
                             text-gray-500 uppercase">
                Doc.
              </th>
              <th className="px-2 py-1.5 text-center text-[10px] font-semibold
                             text-gray-500 uppercase">
                Analyse TICGN
              </th>
            </tr>
          </thead>

          <tbody className="divide-y divide-gray-100">
            {loading ? (
              <tr>
                <td colSpan={7} className="px-3 py-6 text-center text-[10px] text-gray-400">
                  <FiRefreshCw className="inline w-3.5 h-3.5 animate-spin mr-1" />
                  Chargement des contrats…
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-3 py-6 text-center text-[10px] text-gray-400">
                  Aucun contrat signé pour cette période
                </td>
              </tr>
            ) : (
              rows.map(contract => {
                const meta          = parseMetadata(contract.metadata);
                const companyName   = resolveCompanyName(contract);
                const siret         = contract.account?.siret || meta.siret || '—';
                const accountStatus = (contract.account?.status || '').toLowerCase();
                const isClosed      = ['clôturé', 'closed', 'inactif', 'inactive'].includes(accountStatus);

                return (
                  <tr key={contract.id} className="hover:bg-gray-50 transition-colors">

                    {/* CLIENT */}
                    <td className="px-2 py-1.5">
                      <div className="flex items-center gap-1.5">
                        <span className="inline-flex items-center justify-center h-5 w-5 rounded-full
                                         bg-blue-100 text-blue-700 text-[9px] font-semibold flex-shrink-0">
                          {resolveInitials(contract)}
                        </span>
                        <p className="text-[10px] font-medium text-gray-800 leading-tight">
                          {companyName ?? (
                            <span className="text-gray-400 italic">
                              #{contract.account_id?.slice(0, 8)}
                            </span>
                          )}
                        </p>
                      </div>
                    </td>

                    {/* SIRET */}
                    <td className="px-2 py-1.5 text-[10px] text-gray-500 font-mono">
                      {siret}
                    </td>

                    {/* RUM */}
                    <td className="px-2 py-1.5 text-[10px] text-gray-600 font-mono">
                      {meta.rum || '—'}
                    </td>

                    {/* DATE SIGNATURE */}
                    <td className="px-2 py-1.5 text-[10px] text-gray-600">
                      {formatDate(contract.signed_at)}
                    </td>

                    {/* TICGN (toujours actif sur cette table) */}
                    <td className="px-2 py-1.5 bg-blue-50/30">
                      <StatusDot
                        active={true}
                        closed={isClosed}
                        title={isClosed ? 'TICGN — compte clôturé' : 'TICGN — contrat signé'}
                      />
                    </td>

                    {/* DOC SIGNÉ */}
                    <td className="px-2 py-1.5">
                      {contract.signed_document_url ? (
                        <a
                          href={contract.signed_document_url}
                          onClick={(e) => { e.preventDefault(); ouvrirDocumentStocke(contract.signed_document_url); }}
                          target="_blank"
                          rel="noreferrer"
                          title="Voir le document signé"
                          className="inline-flex items-center justify-center w-6 h-6 rounded
                                     bg-blue-50 text-blue-600 hover:bg-blue-100 transition-colors"
                        >
                          <FiFileText className="w-3 h-3" />
                        </a>
                      ) : (
                        <span className="text-[9px] text-gray-300">—</span>
                      )}
                    </td>

                    {/* VOIR ANALYSE TICGN */}
                    <td className="px-2 py-1.5 text-center">
                      <button
                        onClick={() => onSelectClient?.(contract)}
                        title="Voir l'analyse TICGN"
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded
                                   bg-blue-50 text-blue-700 hover:bg-blue-100
                                   text-[10px] font-medium transition-colors border border-blue-200"
                      >
                        <FiBarChart2 className="w-3 h-3" />
                        Analyse
                      </button>
                    </td>

                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default SignedContractsTICGN;
